import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Text,
  Card,
  TextArea,
  ScrollArea,
  IconButton,
  Select,
  Heading,
} from "@radix-ui/themes";
import {
  Send,
  Bot,
  User,
  MessageSquare,
  Plus,
  ArrowLeft,
  Settings,
} from "lucide-react";
import { DigitalOceanAgent } from "@/types/breeze/AIAgent";
import { useAgentChat, ChatMode } from "@/hooks/useAgentChat";

export default function AIAgentChatPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const {
    selectedAgent,
    switchAgent,
    conversations,
    activeConversationId,
    setActiveConversationId,
    messageInput,
    setMessageInput,
    isLoading,
    chatMode,
    setChatMode,
    selectedModel,
    setSelectedModel,
    messagesEndRef,
    agents,
    availableModels,
    activeConversation,
    createNewConversation,
    sendMessage,
    handleKeyPress,
  } = useAgentChat({ initialAgentId: agentId });

  // Auto-create first conversation when agent is selected
  React.useEffect(() => {
    if (selectedAgent && conversations.length === 0) {
      createNewConversation();
    }
  }, [selectedAgent, conversations.length]);

  // Auto-select first conversation when available
  React.useEffect(() => {
    if (conversations.length > 0 && !activeConversationId) {
      setActiveConversationId(conversations[0].id);
    }
  }, [conversations, activeConversationId]);

  return (
    <Box style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        p="4"
        style={{
          borderBottom: "1px solid var(--gray-6)",
          backgroundColor: "var(--gray-2)",
        }}
      >
        <Flex justify="between" align="center" mb="3">
          <Flex align="center" gap="3">
            <IconButton variant="ghost" onClick={() => navigate("/ai-agents")}>
              <ArrowLeft size={20} />
            </IconButton>
            <MessageSquare size={24} />
            <Heading size="6">
              {selectedAgent ? `Chat with ${selectedAgent.name}` : "AI Chat"}
            </Heading>
          </Flex>
        </Flex>

        {/* Controls */}
        <Flex align="center" gap="4" wrap="wrap">
          <Flex align="center" gap="2">
            <Text size="2" weight="medium">
              Agent:
            </Text>
            <Select.Root
              value={selectedAgent?.id || ""}
              onValueChange={(value) => {
                const agent = agents.find(
                  (a: DigitalOceanAgent) => a.id === value,
                );
                if (agent) {
                  switchAgent(agent);
                }
              }}
            >
              <Select.Trigger
                style={{ minWidth: "200px" }}
                placeholder="Select an agent..."
              />
              <Select.Content>
                {agents?.map((agent: DigitalOceanAgent) => (
                  <Select.Item key={agent.id} value={agent.id}>
                    {agent.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>

          <Flex align="center" gap="2">
            <Settings size={16} />
            <Text size="2" weight="medium">
              Mode:
            </Text>
            <Select.Root
              value={chatMode}
              onValueChange={(value: ChatMode) => setChatMode(value)}
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Item value="agent">Agent Chat</Select.Item>
                <Select.Item value="inference">Inference</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>

          {chatMode === "inference" && (
            <Flex align="center" gap="2">
              <Text size="2" weight="medium">
                Model:
              </Text>
              <Select.Root
                value={selectedModel}
                onValueChange={setSelectedModel}
              >
                <Select.Trigger />
                <Select.Content>
                  {availableModels?.map((model) => (
                    <Select.Item key={model} value={model}>
                      {model}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          )}

          <Button onClick={createNewConversation} disabled={!selectedAgent}>
            <Plus size={16} />
            New Chat
          </Button>
        </Flex>
      </Box>

      {/* Main Chat Area */}
      <Flex style={{ flex: 1, overflow: "hidden" }}>
        {/* Conversations Sidebar */}
        <Box
          style={{
            width: "300px",
            borderRight: "1px solid var(--gray-6)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--gray-1)",
          }}
        >
          <Box p="3" style={{ borderBottom: "1px solid var(--gray-6)" }}>
            <Text size="3" weight="medium">
              Conversations
            </Text>
          </Box>

          <ScrollArea style={{ flex: 1 }}>
            <Box p="2">
              {conversations.map((conversation) => (
                <Card
                  key={conversation.id}
                  style={{
                    padding: "12px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    backgroundColor:
                      activeConversationId === conversation.id
                        ? "var(--accent-3)"
                        : "var(--color-surface)",
                  }}
                  onClick={() => setActiveConversationId(conversation.id)}
                >
                  <Text
                    size="2"
                    weight="medium"
                    style={{ display: "block", marginBottom: "4px" }}
                  >
                    {conversation.name}
                  </Text>
                  <Text size="1" color="gray">
                    {conversation.created_at.toLocaleDateString()}
                  </Text>
                </Card>
              ))}

              {conversations.length === 0 && (
                <Box p="4" style={{ textAlign: "center" }}>
                  <MessageSquare
                    size={32}
                    color="var(--gray-8)"
                    style={{ margin: "0 auto 12px" }}
                  />
                  <Text size="2" color="gray">
                    No conversations yet
                  </Text>
                  <Text
                    size="1"
                    color="gray"
                    style={{ display: "block", marginTop: "4px" }}
                  >
                    Start a new chat to begin
                  </Text>
                </Box>
              )}
            </Box>
          </ScrollArea>
        </Box>

        {/* Chat Messages Area */}
        <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {activeConversation ? (
            <>
              <ScrollArea style={{ flex: 1, padding: "20px" }}>
                <Box>
                  {activeConversation.messages.map((message) => (
                    <Flex
                      key={message.id}
                      direction="column"
                      gap="1"
                      mb="4"
                      style={{
                        alignItems:
                          message.role === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Flex align="center" gap="2">
                        {message.role === "assistant" && (
                          <Bot size={16} color="var(--accent-9)" />
                        )}
                        <Text size="1" color="gray">
                          {message.role === "user"
                            ? "You"
                            : selectedAgent?.name || "Assistant"}
                        </Text>
                        {message.role === "user" && (
                          <User size={16} color="var(--gray-9)" />
                        )}
                      </Flex>

                      <Card
                        style={{
                          padding: "12px 16px",
                          maxWidth: "70%",
                          backgroundColor:
                            message.role === "user"
                              ? "var(--accent-3)"
                              : "var(--gray-3)",
                        }}
                      >
                        <Text
                          size="2"
                          style={{ whiteSpace: "pre-wrap", lineHeight: "1.5" }}
                        >
                          {message.content}
                        </Text>
                      </Card>

                      <Text size="1" color="gray">
                        {message.timestamp.toLocaleTimeString()}
                      </Text>
                    </Flex>
                  ))}

                  {isLoading && (
                    <Flex direction="column" gap="1" mb="4">
                      <Flex align="center" gap="2">
                        <Bot size={16} color="var(--accent-9)" />
                        <Text size="1" color="gray">
                          {selectedAgent?.name || "Assistant"}
                        </Text>
                      </Flex>
                      <Card
                        style={{
                          padding: "12px 16px",
                          maxWidth: "70%",
                          backgroundColor: "var(--gray-3)",
                        }}
                      >
                        <Text size="2" color="gray">
                          Thinking...
                        </Text>
                      </Card>
                    </Flex>
                  )}

                  <div ref={messagesEndRef} />
                </Box>
              </ScrollArea>

              {/* Message Input */}
              <Box
                p="4"
                style={{
                  borderTop: "1px solid var(--gray-6)",
                  backgroundColor: "var(--gray-1)",
                }}
              >
                <Flex gap="3">
                  <TextArea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Message ${selectedAgent?.name || "Agent"}...`}
                    style={{ flex: 1, minHeight: "44px", resize: "none" }}
                    disabled={isLoading}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || isLoading}
                    size="3"
                  >
                    <Send size={18} />
                  </Button>
                </Flex>
              </Box>
            </>
          ) : (
            <Box
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Box>
                <Bot
                  size={64}
                  color="var(--gray-8)"
                  style={{ margin: "0 auto 20px" }}
                />
                <Heading size="5" mb="3">
                  {selectedAgent
                    ? `Ready to chat with ${selectedAgent.name}`
                    : "Select an agent to start chatting"}
                </Heading>
                <Text size="3" color="gray" mb="4">
                  {selectedAgent
                    ? "Start a new conversation to begin"
                    : "Choose an agent from the dropdown above"}
                </Text>
                {selectedAgent && (
                  <Button onClick={createNewConversation} size="3">
                    <Plus size={18} />
                    Start New Chat
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
